import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { asaasApi } from '../api/assasApi'
import { COMPANIES_INSTALLMENTS_DATA } from '../constants/companies-installments-data'
import { CreditCardFeesResponse, InstallmentOption } from '../types/payment'
import { getUrlParameter } from '../utils/urlParams'

export type UseCreditCardFeesProps = {
  value: number
}

export type UseCreditCardFeesReturn = {
  installmentOptions: InstallmentOption[]
  isLoading: boolean
}

export type InstallmentsValueResponse = string[]

const getCreditCardFee = (
  installmentNumber: number,
  creditCardFees: CreditCardFeesResponse
): number => {
  if (installmentNumber <= 6)
    return creditCardFees.upToSixInstallmentsPercentage
  if (installmentNumber <= 12)
    return creditCardFees.upToTwelveInstallmentsPercentage
  return creditCardFees.upToTwentyOneInstallmentsPercentage
}

export const useCreditCardFees = ({
  value,
}: UseCreditCardFeesProps): UseCreditCardFeesReturn => {
  const [installmentOptions, setInstallmentOptions] = useState<
    InstallmentOption[]
  >([])
  const [isLoading, setIsLoading] = useState(false)

  const companyId = getUrlParameter('Id')

  useEffect(() => {
    const companyData = COMPANIES_INSTALLMENTS_DATA.find(
      (data) => data.companyId === companyId
    )
    if (!companyData) return

    const getInstallmentsValue = async (): Promise<number[] | undefined> => {
      try {
        setIsLoading(true)
        const creditCardFees = await asaasApi.getCreditCardFees()

        const parsedValue = parseFloat(value.toFixed(2).replace(',', '.'))
        const totalValue = parsedValue + creditCardFees.operationValue
        const installments: InstallmentOption[] = [
          {
            value: '1',
            text: `1x de R$ ${parsedValue
              .toFixed(2)
              .replace('.', ',')} sem juros`,
          },
        ]

        if (companyData.hasInterest) {
          for (let i = 2; i <= companyData.maxInstallments; i++) {
            const creditCardFee = getCreditCardFee(i, creditCardFees)
            const installmentValue =
              (totalValue / i) * (1 + creditCardFee / 100)
            const roundedInstallmentsValue =
              Math.ceil(installmentValue * 100) / 100

            if (roundedInstallmentsValue < 5.0) {
              break
            }

            installments.push({
              value: i.toString(),
              text: `${i}x de R$ ${roundedInstallmentsValue
                .toFixed(2)
                .replace('.', ',')} com juros`,
            })
          }
        } else {
          for (let i = 2; i <= companyData.maxInstallments; i++) {
            const installmentValue = parsedValue / i
            const roundedInstallmentsValue =
              Math.ceil(installmentValue * 100) / 100

            if (roundedInstallmentsValue < 5.0) {
              break
            }
            installments.push({
              value: i.toString(),
              text: `${i}x de R$ ${roundedInstallmentsValue
                .toFixed(2)
                .replace('.', ',')} sem juros`,
            })
          }
        }
        setInstallmentOptions(installments)
        setIsLoading(false)
      } catch {
        setIsLoading(false)
        toast.error('Erro ao obter valor das parcelas. Tente novamente.')
        return
      }
    }
    getInstallmentsValue()
  }, [companyId, value])

  return {
    installmentOptions,
    isLoading,
  }
}
